import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MembershipBadge } from "@/components/MembershipBadge";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

const PLAN_CARDS = [
    {
        key: null,
        name: "Free",
        tagline: "Browse, search, contribute to the archive.",
        features: [
            "Unlimited browsing & search",
            "View all historical & user encounters",
            "Comment, like, save encounters",
            "Submit standard encounters",
            "Light + dark theme",
        ],
        icon: ShieldCheck,
    },
    {
        key: "verified_witness",
        name: "Verified Witness",
        tagline: "For experiencers, witnesses, and serious investigators.",
        features: [
            "Silver Witness shield insignia on profile + comments",
            "Witness credibility boost (verified mark)",
            "Photo & video uploads on submissions",
            "Comment attachments (photos + clips)",
            "Submit abduction / high-review cases",
            "AI encounter reconstruction (entity + craft)",
            "Private encounter journal",
            "Timeline tracking",
        ],
        badgeTier: "verified_witness",
    },
    {
        key: "research_council",
        name: "Research Council",
        tagline: "Trusted Source · For analysts, journalists, academics.",
        features: [
            "Everything in Verified Witness",
            "Gold Research Council emblem · Trusted Source",
            "Elevated comments (always float to top of threads)",
            "Larger uploads + extended media retention",
            "Research Collections (bookmark sets + briefs)",
            "Research analytics dashboard",
            "Bulk search, export & API access",
            "Higher visibility across the platform",
        ],
        highlight: true,
        badgeTier: "research_council",
    },
];

export default function Pricing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [interval, setInterval] = useState("annual"); // monthly | annual
    const [loadingKey, setLoadingKey] = useState(null);
    const [plans, setPlans] = useState([]);
    const [promoInput, setPromoInput] = useState("");
    const [promo, setPromo] = useState(null);     // resolved promo {code, percent_off, success_message, discounted_amount, original_amount, first_period_only}
    const [promoError, setPromoError] = useState("");
    const [promoLoading, setPromoLoading] = useState(false);

    useEffect(() => {
        api.get("/billing/plans").then((r) => setPlans(r.data.plans || []));
        track("pricing_view");
    }, []);

    // Recompute promo against current interval so switching plans re-validates eligibility
    useEffect(() => {
        // Only monthly plans accept the current promos, clear on switch to annual
        if (promo && interval !== "monthly") {
            setPromo(null);
            setPromoError("This code only applies to monthly plans.");
        }
    }, [interval, promo]);

    const planLookup = Object.fromEntries(plans.map((p) => [p.key, p]));

    const applyPromo = async () => {
        const code = (promoInput || "").trim();
        if (!code) return;
        // Validate against the FIRST monthly plan (the codes apply to all monthly plans)
        const sampleMonthlyKey = "research_council_monthly";
        setPromoLoading(true);
        setPromoError("");
        try {
            const { data } = await api.post("/billing/validate-promo", {
                code,
                plan_key: sampleMonthlyKey,
            });
            setPromo(data);
            toast.success(data.success_message);
        } catch (e) {
            setPromo(null);
            setPromoError(formatApiErrorDetail(e.response?.data?.detail) || "Promo code is not valid");
        } finally {
            setPromoLoading(false);
        }
    };

    const clearPromo = () => {
        setPromo(null);
        setPromoInput("");
        setPromoError("");
    };

    const upgrade = async (tier) => {
        if (!user) {
            toast.error("Sign in to upgrade");
            navigate("/login");
            return;
        }
        const key = `${tier}_${interval}`;
        const plan = planLookup[key];
        const amount = Number((promo && interval === "monthly" ? promo.discounted_amount : plan?.amount) || 0);
        if (interval === "annual") {
            track("annual_membership_selected", { tier });
        }
        track("checkout_started", {
            plan_key: key,
            tier,
            interval,
            value: amount,
            currency: (plan?.currency || "usd").toUpperCase(),
        });
        setLoadingKey(key);
        try {
            const body = {
                plan_key: key,
                origin_url: window.location.origin,
            };
            if (promo && interval === "monthly") body.promo_code = promo.code;
            const { data } = await api.post("/billing/checkout", body);
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
            setLoadingKey(null);
        }
    };

    return (
        <div className="ok-container py-12" data-testid="pricing-page">
            <div className="text-center max-w-2xl mx-auto mb-10">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]" data-testid="pricing-founding-label">
                    Founding Member Pricing
                </div>
                <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-tight">
                    Become a <span className="text-[var(--ok-accent)]">Founding Witness</span>
                </h1>
                <p className="opacity-70 mt-3 text-sm leading-relaxed">
                    Founding members lock in this price for life, receive a permanent badge,
                    priority moderation, and early access. Pricing is genuine and rises as the archive grows.
                </p>
            </div>

            {/* Toggle */}
            <div className="flex justify-center mb-4" data-testid="pricing-interval-toggle">
                <div className="ok-card p-1 inline-flex">
                    <button onClick={() => setInterval("monthly")} className={`px-5 py-2 text-xs font-bold uppercase tracking-widest rounded-sm ${interval === "monthly" ? "bg-[var(--ok-accent)] text-[color:var(--ok-on-accent)]" : "opacity-60"}`} data-testid="interval-monthly">Monthly</button>
                    <button onClick={() => setInterval("annual")} className={`px-5 py-2 text-xs font-bold uppercase tracking-widest rounded-sm flex items-center gap-2 ${interval === "annual" ? "bg-[var(--ok-accent)] text-[color:var(--ok-on-accent)]" : "opacity-60"}`} data-testid="interval-annual">
                        Annual <span className="text-[10px] px-1.5 rounded-sm" style={{ color: interval === "annual" ? "var(--ok-accent)" : "var(--ok-on-accent)", background: interval === "annual" ? "var(--ok-on-accent)" : "var(--ok-accent)" }}>SAVE 43%</span>
                    </button>
                </div>
            </div>

            {/* Promo code input — monthly plans only */}
            <div className="flex justify-center mb-10" data-testid="pricing-promo-row">
                <div className="ok-card p-3 inline-flex items-center gap-2 max-w-md w-full">
                    {promo ? (
                        <div className="flex-1 flex items-center justify-between gap-3" data-testid="pricing-promo-applied">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">Promo applied</div>
                                <div className="text-xs opacity-80 mt-0.5">{promo.success_message}</div>
                            </div>
                            <button onClick={clearPromo} className="ok-btn-outline text-xs px-3 py-1" data-testid="pricing-promo-clear">Remove</button>
                        </div>
                    ) : (
                        <>
                            <input
                                type="text"
                                placeholder="Enter promo code"
                                value={promoInput}
                                onChange={(e) => { setPromoInput(e.target.value); setPromoError(""); }}
                                onKeyDown={(e) => { if (e.key === "Enter") applyPromo(); }}
                                className="ok-input flex-1 text-sm tracking-wider uppercase"
                                data-testid="pricing-promo-input"
                                disabled={promoLoading}
                            />
                            <button
                                onClick={applyPromo}
                                disabled={promoLoading || !promoInput.trim()}
                                className="ok-btn-primary text-xs px-4"
                                data-testid="pricing-promo-apply"
                            >
                                {promoLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
                            </button>
                        </>
                    )}
                </div>
            </div>
            {promoError && (
                <div className="text-center -mt-7 mb-7 text-xs text-rose-400" data-testid="pricing-promo-error">{promoError}</div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 max-w-6xl mx-auto" data-testid="pricing-grid">
                {PLAN_CARDS.map((card) => {
                    const Icon = card.icon;
                    const key = card.key ? `${card.key}_${interval}` : null;
                    const plan = key ? planLookup[key] : null;
                    return (
                        <div key={card.name} className={`ok-card p-7 flex flex-col relative ${card.highlight ? "lg:scale-105 lg:z-10" : ""}`}
                             style={card.highlight ? { borderColor: "#e7c873", boxShadow: "0 0 0 1px #e7c873" } : (card.badgeTier === "verified_witness" ? { borderColor: "#c2c6cb" } : {})}
                             data-testid={`pricing-card-${card.name.toLowerCase().replace(" ", "-")}`}>
                            {card.highlight && (
                                <span className="absolute -top-3 left-1/2 -translate-x-1/2 ok-tag font-mono text-[10px] uppercase tracking-widest" style={{ background: "#e7c873", color: "#1a140a", borderColor: "#e7c873" }}>
                                    Trusted Source
                                </span>
                            )}
                            {card.badgeTier ? (
                                <MembershipBadge tier={card.badgeTier} size="lg" />
                            ) : Icon ? (
                                <Icon className="w-6 h-6 text-[var(--ok-accent)]" />
                            ) : null}
                            <h3 className="font-display text-2xl mt-3">{card.name}</h3>
                            <p className="text-xs opacity-70 mt-1 min-h-[2.5rem]">{card.tagline}</p>

                            <div className="my-5">
                                {!card.key ? (
                                    <div>
                                        <div className="font-display text-4xl">$0</div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-1">Forever free</div>
                                    </div>
                                ) : plan ? (
                                    <div>
                                        {interval === "annual" && plan.std_amount && (
                                            <div className="text-sm opacity-50 line-through">${plan.std_amount.toFixed(2)} / year</div>
                                        )}
                                        {interval === "monthly" && promo && (
                                            <div className="text-sm opacity-50 line-through">${plan.amount.toFixed(2)} / month</div>
                                        )}
                                        <div className="font-display text-4xl text-[var(--ok-accent)]">
                                            ${(interval === "monthly" && promo
                                                ? plan.amount * (1 - promo.percent_off / 100)
                                                : plan.amount
                                            ).toFixed(2)}
                                            <span className="text-sm opacity-60 font-sans font-normal ml-1">/ {interval === "annual" ? "year" : "month"}</span>
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)] mt-1">
                                            {interval === "annual"
                                                ? "Founding price · locked in for life"
                                                : (promo ? `${promo.code} · 10% off first month` : "Cancel anytime")}
                                        </div>
                                    </div>
                                ) : (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                )}
                            </div>

                            <ul className="space-y-2 text-sm flex-1">
                                {card.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 opacity-90">
                                        <Check className="w-4 h-4 text-[var(--ok-accent)] flex-shrink-0 mt-0.5" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => card.key ? upgrade(card.key) : navigate("/signup")}
                                disabled={loadingKey === key || (user && user.subscription_tier === card.key)}
                                className={`mt-6 w-full justify-center ${card.highlight || card.key ? "ok-btn-primary" : "ok-btn-outline"}`}
                                data-testid={`pricing-cta-${card.name.toLowerCase().replace(" ", "-")}`}
                            >
                                {loadingKey === key ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                {user && user.subscription_tier === card.key ? "Current plan"
                                    : card.key ? "Become a Founding Witness"
                                    : "Get started free"}
                            </button>
                        </div>
                    );
                })}
            </div>

            <p className="text-center text-xs opacity-50 mt-10 max-w-xl mx-auto">
                Founding Member pricing available while the platform is in early growth. No deceptive countdowns. Pricing rises naturally as the archive scales. Cancel anytime.
            </p>
        </div>
    );
}
